/**
 * The generated `/ai` DTOs read into the app's shapes (`conversation.ts`), and the one write the
 * web makes to a built plan before it opens: the trainer's resolutions applied to its slots.
 *
 * Pure. The wire's strings are matched exactly as the backend documents them
 * (`Modules/Ai/Dtos/Responses/AiTurnResponses.cs`); an unknown one reads as the safest case (a turn
 * of unknown status is still running, so it is polled rather than drawn as finished).
 */

import type {
  TrainWithMeModulesAiDtosRequestsAiAnswerInput as AnswerDto,
  TrainWithMeModulesAiDtosResponsesAiConversationResponse as ConversationDto,
  TrainWithMeModulesAiDtosResponsesAiDraftResult as DraftDto,
  TrainWithMeModulesAiDtosResponsesAiExerciseCandidateResponse as CandidateDto,
  TrainWithMeModulesAiDtosResponsesAiProposedExerciseResponse as ProposedDto,
  TrainWithMeModulesAiDtosResponsesAiStatusResponse as StatusDto,
  TrainWithMeModulesAiDtosResponsesAiTurnResponse as TurnDto,
  TrainWithMeModulesPlansDtosResponsesPlanTemplateFullResponse as PlanDto,
} from '~/shared/api/generated/model';
import { measurementFieldsFromWire } from '~/shared/measurement';

import type {
  AiAnswer,
  AiBuildResult,
  AiCandidateRelation,
  AiChangeDecision,
  AiConversation,
  AiExerciseCandidate,
  AiExerciseDecision,
  AiMatchVerdict,
  AiProposedExercise,
  AiSchedulingMode,
  AiTurn,
  AiTurnInput,
  AiTurnKind,
  AiTurnResult,
  AiTurnStatus,
  AiUsageMeter,
} from './conversation';

const KINDS: Readonly<Record<string, AiTurnKind>> = {
  Ask: 'ask',
  Outline: 'outline',
  Build: 'build',
  Answer: 'answer',
  Propose: 'propose',
  Import: 'import',
};

const DECISIONS: Readonly<Record<string, AiChangeDecision>> = { accepted: 'accepted', rejected: 'rejected', applied: 'applied' };

function schedulingMode(value: string | null | undefined): AiSchedulingMode {
  return value === 'SelfPaced' ? 'selfPaced' : 'dateBased';
}

function answerFromWire(answer: AnswerDto): AiAnswer {
  return answer.optionId !== undefined && answer.optionId !== null && answer.optionId !== ''
    ? { questionId: answer.questionId, kind: 'option', optionId: answer.optionId }
    : { questionId: answer.questionId, kind: 'text', text: answer.text ?? '' };
}

function inputFromWire(turn: TurnDto): AiTurnInput {
  const input = turn.input;
  if (input === null) return { kind: 'none' };
  if (input.import !== undefined && input.import !== null) {
    return { kind: 'import', fileName: input.import.fileName, sheets: input.import.sheets };
  }
  if (input.skippedQuestions === true) return { kind: 'skipQuestions' };
  if (input.answers !== undefined && input.answers !== null && input.answers.length > 0) {
    const words = input.text?.trim() ?? '';
    return { kind: 'answers', answers: input.answers.map(answerFromWire), text: words === '' ? null : words };
  }
  if (input.text !== undefined && input.text !== null && input.text.trim() !== '') {
    const rejected = input.rejectedChangeId ?? null;
    return rejected === null ? { kind: 'message', text: input.text } : { kind: 'message', text: input.text, rejectedChangeId: rejected };
  }
  return { kind: 'none' };
}

type Slot = ReturnType<typeof slots>[number];

/** Every exercise slot of a plan, with the day it stands in. */
function slots(plan: PlanDto) {
  return (plan.phases ?? []).flatMap((phase) =>
    (phase.days ?? []).flatMap((day) =>
      (day.sessions ?? []).flatMap((session) =>
        (session.exercises ?? []).map((exercise) => ({ day: day.name ?? '', exercise })),
      ),
    ),
  );
}

function decisionFromWire(kind: string | null | undefined, exerciseId: number | null | undefined): AiExerciseDecision | null {
  if (kind === 'skipped') return { kind: 'skip' };
  if (kind === 'picked' && typeof exerciseId === 'number' && exerciseId > 0) return { kind: 'use', exerciseId };
  return null;
}

/**
 * Buddy's proposal for a name, or `null` when there is none to add as it stands: absent (a draft
 * stored before proposals, a name Buddy matched by itself) or without a type, which the creation
 * refuses. A measurement field this build does not know is dropped by the one parser.
 */
function verdictFromWire(verdict: string | null | undefined): AiMatchVerdict | null {
  return verdict === 'near' || verdict === 'none' ? verdict : null;
}

/** A candidate as the list offers it; a difference is read only where the candidate is near. */
function candidateFromWire(candidate: CandidateDto): AiExerciseCandidate {
  const relation: AiCandidateRelation | null = candidate.relation === 'same' || candidate.relation === 'near' ? candidate.relation : null;
  const difference = relation === 'near' ? (candidate.difference ?? '').trim() : '';
  return { exerciseId: candidate.exerciseId, name: candidate.name ?? '', relation, difference: difference === '' ? null : difference };
}

/** The same movement under another name leads; otherwise the server's order (best match first) stands. */
function candidatesFromWire(candidates: readonly CandidateDto[]): AiExerciseCandidate[] {
  const read = candidates.map(candidateFromWire);
  return [...read.filter((candidate) => candidate.relation === 'same'), ...read.filter((candidate) => candidate.relation !== 'same')];
}

function blankAsNull(text: string | null | undefined): string | null {
  const trimmed = text?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

function proposedFromWire(proposal: ProposedDto | null | undefined): AiProposedExercise | null {
  if (proposal === null || proposal === undefined || proposal.exerciseType.trim() === '') return null;
  return {
    exerciseType: proposal.exerciseType,
    primaryMuscle: proposal.primaryMuscle ?? null,
    equipment: proposal.equipment.map(({ id, code }) => ({ id, code })),
    measurementFields: measurementFieldsFromWire(proposal.measurementFields),
    // Both absent on a draft stored before Buddy wrote them; null, blank and [] all mean none.
    description: blankAsNull(proposal.description),
    instructions: (proposal.instructions ?? []).map((step) => step.trim()).filter((step) => step !== ''),
  };
}

/** The name the server wrote into a picked name's slots: the plan is its answer, never patched here. */
function pickedNameOf(all: readonly Slot[], tokens: ReadonlySet<string>, exerciseId: number | null | undefined): string | null {
  if (typeof exerciseId !== 'number' || exerciseId <= 0) return null;
  const slot = all.find((each) => tokens.has(each.exercise.slotToken ?? '') && each.exercise.exerciseId === exerciseId);
  const name = slot?.exercise.exerciseName?.trim() ?? '';
  return name === '' ? null : name;
}

function buildFromWire(draft: DraftDto): AiBuildResult | null {
  const plan = draft.plan;
  if (plan === null) return null;
  const all = slots(plan);
  const daysOf = (tokens: ReadonlySet<string>) => [
    ...new Set(all.filter((slot) => tokens.has(slot.exercise.slotToken ?? '')).map((slot) => slot.day)),
  ];
  const firstPhase = (plan.phases ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
  return {
    title: plan.name ?? '',
    cycleCount: (plan.phases ?? []).reduce((total, phase) => total + Math.max(1, phase.cycleCount ?? 1), 0),
    schedulingMode: schedulingMode(plan.schedulingMode),
    sessionsPerCycle: firstPhase?.days?.length ?? 0,
    catalogueExercises: new Set(
      all.map((slot) => slot.exercise.exerciseId ?? 0).filter((exerciseId) => exerciseId > 0),
    ).size,
    unresolved: (draft.unresolved ?? []).map((item) => {
      const tokens = new Set(item.slotTokens ?? []);
      return {
        name: item.name ?? '',
        slotTokens: [...tokens],
        days: daysOf(tokens),
        candidates: candidatesFromWire(item.candidates ?? []),
        verdict: verdictFromWire(item.verdict),
        decision: decisionFromWire(item.resolution?.kind, item.resolution?.exerciseId),
        problems: item.problems ?? [],
        proposedExercise: proposedFromWire(item.proposedExercise),
        pickedName: pickedNameOf(all, tokens, item.resolution?.exerciseId),
      };
    }),
    // Absent from a draft an older server stored: Buddy made no pick of its own then.
    autoMatched: (draft.autoMatched ?? []).map((match) => {
      const tokens = new Set(match.slotTokens);
      return {
        name: match.name,
        exerciseId: match.exerciseId,
        exerciseName: match.exerciseName,
        slotTokens: [...tokens],
        days: daysOf(tokens),
        // The pick is the one a change starts from, so it leads even where the closest ones left it out.
        candidates: [
          { exerciseId: match.exerciseId, name: match.exerciseName, relation: null, difference: null },
          ...candidatesFromWire(match.candidates.filter((candidate) => candidate.exerciseId !== match.exerciseId)),
        ],
      };
    }),
    summary: draft.summary ?? '',
    assumptions: draft.assumptions ?? [],
    startWeekday: draft.startWeekday ?? null,
    revision: draft.revision ?? 0,
    warnings: (draft.warnings ?? []).map((warning) => ({
      exerciseName: warning.exerciseName ?? '',
      message: warning.message ?? '',
    })),
    plan,
    // Absent from a turn an older server wrote: nothing to decide then.
    valuesToDecide: (draft.valuesToDecide ?? []).map(({ sheet, cell, value, note }) => ({ sheet, cell, value, note })),
  };
}

function resultFromWire(turn: TurnDto): AiTurnResult | null {
  const result = turn.result;
  if (result === undefined || result === null) return null;
  switch (result.type) {
    case 'questions':
      return {
        kind: 'questions',
        intro: result.questions?.intro ?? null,
        questions: (result.questions?.items ?? []).map((question) => ({
          id: question.id ?? '',
          text: question.text ?? '',
          options: (question.options ?? []).map((option) => ({ id: option.id ?? '', label: option.label ?? '' })),
        })),
      };
    case 'answer':
      return { kind: 'answer', text: result.text ?? '' };
    case 'refusal':
      return {
        kind: 'refusal',
        reason: result.refusal?.reason === 'health' ? 'health' : 'offTopic',
        text: result.refusal?.text ?? '',
      };
    case 'outline': {
      const outline = result.outline;
      if (outline === undefined || outline === null) return null;
      return {
        kind: 'outline',
        outline: {
          title: outline.title ?? '',
          daysPerWeek: outline.daysPerWeek,
          cycleCount: outline.totalCycles,
          schedulingMode: schedulingMode(outline.schedulingMode),
          startWeekday: outline.startWeekday ?? null,
          phases: (outline.phases ?? []).map((phase) => ({
            name: phase.name ?? '',
            line: phase.line ?? '',
            cycleCount: phase.cycles,
            lightweight: phase.type === 'Deload',
          })),
          assumptions: outline.assumptions ?? [],
        },
      };
    }
    case 'draft': {
      const built = result.draft === undefined || result.draft === null ? null : buildFromWire(result.draft);
      return built === null ? null : { kind: 'build', result: built };
    }
    case 'proposal':
      return result.proposal === undefined || result.proposal === null ? null : { kind: 'proposal', proposal: result.proposal };
    default:
      return null;
  }
}

function statusFromWire(turn: TurnDto, result: AiTurnResult | null): AiTurnStatus {
  switch (turn.status) {
    case 'Queued':
      return 'queued';
    case 'Succeeded':
      if (turn.result?.type === 'notBuilt') return 'failed';
      return result?.kind === 'refusal' ? 'refused' : result === null ? 'failed' : 'done';
    case 'Failed':
      return 'failed';
    case 'Cancelled':
      return 'cancelled';
    default:
      return 'running';
  }
}

export function turnFromWire(turn: TurnDto): AiTurn {
  const result = resultFromWire(turn);
  const status = statusFromWire(turn, result);
  const problems = turn.result?.type === 'notBuilt' ? (turn.result.problems ?? []) : [];
  return {
    id: turn.id,
    kind: KINDS[turn.kind ?? ''] ?? 'answer',
    status,
    input: inputFromWire(turn),
    step: status === 'queued' || status === 'running' ? (turn.step ?? null) : null,
    // The text alone: a line's `at` is never shown (owner, 10 October 2026), and a blank one says nothing.
    log: (turn.log ?? []).flatMap((line) => (line.text === null || line.text.trim() === '' ? [] : [line.text])),
    // A cancelled turn keeps what it had already shown: that is the outline the trainer dismissed.
    result: status === 'done' || status === 'refused' || status === 'cancelled' ? result : null,
    failure: problems.length > 0 ? problems.join(' ') : null,
    failureCode: status === 'failed' ? (turn.failureCode ?? null) : null,
    parentTurnId: turn.parentTurnId ?? null,
    feedback: turn.feedbackHelpful ?? null,
    draftTurnId: turn.input?.draftTurnId ?? null,
    revisesTurnId: turn.input?.revisesTurnId ?? null,
    decisions: new Map(
      (turn.decisions ?? []).flatMap((entry) => {
        const decision = DECISIONS[entry.decision];
        return decision === undefined ? [] : [[entry.changeId, decision] as const];
      }),
    ),
  };
}

/** No window at 80% or more: the meter the thread reads while nothing is near the limit. */
export const QUIET_METER: AiUsageMeter = { percent: 0, window: 'fiveHour', resetsAt: null, blocked: false };

/** The status read's meter (R43): a refusing window first, else the fuller one, else quiet. */
export function meterFromStatus(status: StatusDto | undefined): AiUsageMeter {
  const meters = status?.meters ?? [];
  const chosen =
    meters.find((meter) => meter.blocked) ??
    meters.slice().sort((a, b) => b.usedPercent - a.usedPercent)[0];
  if (chosen === undefined) {
    return status?.blocked === true ? { ...QUIET_METER, percent: 100, blocked: true, resetsAt: status.resetsAt ?? null } : QUIET_METER;
  }
  return {
    percent: Math.round(chosen.usedPercent),
    window: chosen.window === 'weekly' ? 'weekly' : 'fiveHour',
    resetsAt: chosen.resetsAt ?? status?.resetsAt ?? null,
    blocked: chosen.blocked || status?.blocked === true,
  };
}

export function conversationFromWire(conversation: ConversationDto, meter: AiUsageMeter): AiConversation {
  return {
    id: conversation.id,
    clientId: typeof conversation.customerId === 'number' && conversation.customerId > 0 ? conversation.customerId : null,
    draftKey: typeof conversation.draftKey === 'string' && conversation.draftKey !== '' ? conversation.draftKey : null,
    turns: (conversation.turns ?? []).map(turnFromWire),
    meter,
    waitingOutlineTurnId: conversation.waitingOutlineTurnId ?? null,
    openQuestionsTurnId: conversation.openQuestionsTurnId ?? null,
    waitingProposalTurnId: conversation.waitingProposalTurnId ?? null,
  };
}

/**
 * The built plan with the trainer's decisions in it (R8), ready for the builder, or `null` while it
 * is not.
 *
 * A pick is applied by the SERVER: the resolutions route rewrites the slot from the catalogue
 * exercise (its name, type and measurement fields) and returns the turn with the updated plan, so
 * this reads that plan and never patches a slot's identity itself. A skip leaves the plan here,
 * and a session emptied by it keeps no gap. `null` while any name is undecided, and `null` while
 * any slot that was not skipped still carries exercise id 0: the save refuses one, so a draft with
 * one must never reach the builder.
 */
export function resolvedPlan(result: AiBuildResult): PlanDto | null {
  if (result.unresolved.some((item) => item.decision === null)) return null;
  const skipped = new Set(
    result.unresolved.filter((item) => item.decision?.kind === 'skip').flatMap((item) => item.slotTokens),
  );
  const plan: PlanDto = {
    ...result.plan,
    phases: (result.plan.phases ?? []).map((phase) => ({
      ...phase,
      days: (phase.days ?? []).map((day) => ({
        ...day,
        sessions: (day.sessions ?? []).map((session) => ({
          ...session,
          exercises: (session.exercises ?? [])
            .filter((exercise) => !skipped.has(exercise.slotToken ?? ''))
            .map((exercise, index) => ({ ...exercise, order: index + 1 })),
        })),
      })),
    })),
  };
  return slots(plan).some((slot) => (slot.exercise.exerciseId ?? 0) === 0) ? null : plan;
}
